/**
 * Web implementation of BiometricAuth using WebAuthn API.
 *
 * WebAuthn provides biometric authentication on web via platform
 * authenticators (Touch ID, Windows Hello, etc.). Falls back gracefully
 * when unavailable.
 *
 * @module platform/biometrics.web
 */

import type { BiometricAuth } from './types';

/**
 * Create a web BiometricAuth implementation using WebAuthn.
 *
 * @returns BiometricAuth interface backed by WebAuthn
 */
export function createWebBiometricAuth(): BiometricAuth {
  return {
    async isAvailable(): Promise<boolean> {
      if (typeof window === 'undefined') return false;
      if (!('PublicKeyCredential' in window)) return false;

      try {
        // Check if platform authenticator (biometric) is available
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        return available;
      } catch {
        return false;
      }
    },

    async authenticate(reason: string): Promise<boolean> {
      if (typeof window === 'undefined') return false;
      if (!('PublicKeyCredential' in window)) return false;

      try {
        // WebAuthn authentication flow
        // Full implementation will be added in Phase 2 (wallet lifecycle)
        // when credential creation and assertion are needed.
        // The reason parameter will be shown in the WebAuthn prompt.
        void reason; // Used in full implementation (Phase 2)
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        return available;
      } catch {
        return false;
      }
    },
  };
}
